import { _ } from "../../../core/polyglot/polyglot.js";
import { eventCategory } from "@foretagsplatsen/yaem";
import { widget } from "@foretagsplatsen/widgetjs";
import AgencyImportCommand from "../../../models/accounting/document/entryCommands/AgencyImportCommand.js";
import CreateDirectoryCommand from "../../../models/accounting/document/entryCommands/CreateDirectoryCommand.js";
import EmptyArchiveWidget from "../../../core/widgets/emptyArchive/EmptyArchiveWidget.js";
import entrySorter from "../folders/entrySorter.js";
import FileDropWidget from "../../../core/widgets/FileDropWidget.js";
import FileUploadButtonWidget from "../../../core/widgets/FileUploadButtonWidget.js";
import fileWidget from "./fileWidget.js";
import Folder from "../../../models/accounting/document/Folder.js";
import folderWidget from "./folderWidget.js";
import MultipleUploadCommand from "../../../models/accounting/document/entryCommands/MultipleUploadCommand.js";
import notifier from "../../../core/widgets/notifier/notifier.js";
import selectionEventsManager from "../folders/selectionEventsManager.js";
import UploadCommand from "../../../models/accounting/document/entryCommands/UploadCommand.js";
import UploadService from "../../../core/services/UploadService.js";

/**
 * I am an abstract implementation of widget used to display the entries
 * of the current folder.
 *
 * See `iconicFolderView` and `listFolderView` for concrete implementations.
 */

/**
 * Todo: Add JSDoc comment
 */
function abstractFolderView(spec, my) {
	spec ||= {};
	my ||= {};
	const that = widget(spec, my);

	my.sorter = entrySorter;
	my.navigate = spec.navigate;
	my.document = spec.document;

	my.documentService = spec.documentService;

	my.currentFolder = new Folder({ isRoot: true });

	my.eventHandler = eventCategory();

	my.emptyWidget = new EmptyArchiveWidget({
		title: _("This folder is empty"),
		logo: (html) => html.i({ klass: "fas fa-cloud-upload-alt" }),
		actions: (html) => html.render(my.emptyActionsRenderer()),
	});

	my.emptyArchiveWidget = new EmptyArchiveWidget({
		title: _("No files or folders"),
		subtitle: _("Come on, let's get started!"),
		actions: (html) => html.render(my.emptyActionsRenderer({ root: true })),
	});

	my.fileDropArea = new FileDropWidget({
		uploadPath: my.documentService.uploadPath(),
		contents: (html) => {
			// The if statement here is to keep the visualization mode
			// even after encountering an empty folder
			// Setting the currentWidget to `emptyWidget` leads to the loss
			// of this information
			if (my.currentFolder.hasChildren()) {
				renderViewOn(html);
			} else {
				renderEmptyOn(html);
			}
		},
	});

	my.fileDropArea.filesDropped.register(
		({ files, fileUploadingCallback }) => {
			my.handleFilesDropped({ files, fileUploadingCallback });
		},
	);

	that.setCurrentFolder = function (folder) {
		my.currentFolder = folder;
		that.getBreadcrumbs().setCurrentFolder(folder);
	};

	that.getBreadcrumbs = function () {
		if (!my.breadcrumbs) {
			createBreadcrumbs();
		}
		return my.breadcrumbs;
	};

	that.onFileClicked = my.eventHandler.createEvent("fileClicked");

	that.onFolderClicked = my.eventHandler.createEvent("folderClicked");

	that.onSetListView = my.eventHandler.createEvent("setListView");

	that.onSetIconicView = my.eventHandler.createEvent("setIconicView");

	// TODO: Rename to `onEntryWidgetClicked`
	that.onEntryWidgetClick = my.eventHandler.createEvent("entryWidgetClicked");

	that.onNavigateTo = my.eventHandler.createEvent("navigateTo");

	that.refreshContents = my.eventHandler.createEvent("refreshContents");

	that.buildFileWidgetFor = function (aFile, position) {
		return fileWidget(
			{
				model: aFile,
				position,
			},
			{},
		);
	};

	that.buildFolderWidgetFor = function (aFile, position) {
		return folderWidget(
			{
				model: aFile,
				position,
			},
			{},
		);
	};

	that.renderContentOn = function (html) {
		html.render(that.getBreadcrumbs());
		html.render(my.fileDropArea);
	};

	my.registerToEntryClick = function (entryWidget) {
		entryWidget.register("fileClicked", (entry) => {
			my.eventHandler.trigger("fileClicked", entryWidget, entry);
		});

		entryWidget.register("folderClicked", (entry) => {
			my.eventHandler.trigger("folderClicked", entryWidget, entry);
		});
	};

	my.renderBackOn = function (html) {
		if (my.currentFolder.isRoot()) {
			return;
		}
		my.renderBackToParentOn(html);
	};

	function renderViewOn(html) {
		html.div({ klass: my.cssClass() }).render((html) => {
			const entries = my.currentFolder.children();

			my.sorter.sort(entries);

			const widgets = entries.map((entry, index) =>
				buildWidgetForEntry(entry, index),
			);

			my.renderEntryWidgetsOn(widgets, html);
			selectionEventsManager.setWidgets(widgets);
		});

		selectionEventsManager.setupEventsForView(html.root);
	}

	function renderEmptyOn(html) {
		if (my.currentFolder.isRoot()) {
			html.render(my.emptyArchiveWidget);
		} else {
			html.render(my.emptyWidget);
		}
	}

	my.emptyActionsRenderer = function ({ root = false } = {}) {
		const uploadService = new UploadService({
			uploadPath: my.documentService.uploadPath(),
		});

		return (html) => {
			html.a({ klass: "btn" }, _("+ Create folder")).click(
				my.createDirectory,
			);

			html.render(
				new FileUploadButtonWidget({
					cssClass: "btn",
					label: _("+ Upload file"),
					onUpload: uploadService.uploadFile.bind(uploadService),
					onSuccess: (file) => my.uploadFile(file),
					onError: ({ error }) => {
						notifier.error(error.message);
					},
				}),
			);

			if (root) {
				html.a({ klass: "btn" }, _("+ Import archive structure")).click(
					my.agencyImport,
				);
			}
		};
	};

	my.uploadFile = function (file) {
		const command = new UploadCommand({
			documentService: my.documentService,
		});

		command.onRefresh.register(() => {
			my.document.setFolder(my.currentFolder.id());
		});

		command.setName(file.name);
		command.perform();
	};

	my.agencyImport = function () {
		const command = new AgencyImportCommand({
			documentService: my.documentService,
		});

		command.onRefresh.register(() => {
			my.document.setFolder(my.currentFolder.id());
		});

		command.perform();
	};

	my.createDirectory = function () {
		const command = new CreateDirectoryCommand({
			documentService: my.documentService,
		});

		command.onRefresh.register(() => {
			my.document.setFolder(my.currentFolder.id());
		});

		command.perform();
	};

	my.handleFilesDropped = function ({ files, fileUploadingCallback }) {
		const command = new MultipleUploadCommand({
			documentService: my.documentService,
			fileUploadingCallback,
		});

		command.onRefresh.register(that.refreshContents.trigger);
		command.setFiles(files);
		command.doPerform();
	};

	my.backToParent = function () {
		my.triggerNavigateTo(my.currentFolder.parent());
	};

	my.registerToMouseEvents = function (entryWidget) {
		const entry = entryWidget.model();

		entryWidget.click((event) => {
			my.eventHandler.trigger("entryWidgetClicked", entryWidget, event);
		});

		entryWidget.onMouseMove(() => {
			selectionEventsManager.selectOnMouseMove({
				entry,
				widget: entryWidget,
				isShiftSelection: false,
			});
		});
	};

	my.triggerNavigateTo = function (folder) {
		my.eventHandler.trigger("navigateTo", folder);
	};

	my.buildBreadcrumbs = function () {};

	my.renderEntryWidgetsOn = function (_widgets, _html) {};

	my.renderBackToParentOn = function (_html) {};

	my.cssClass = function () {
		return "";
	};

	function buildWidgetForEntry(entry, index) {
		return entry.buildWidgetFor(that, index);
	}

	function createBreadcrumbs() {
		my.breadcrumbs = my.buildBreadcrumbs();

		my.breadcrumbs.onSetIconicView.register(() => {
			my.eventHandler.trigger("setIconicView");
		});

		my.breadcrumbs.onSetListView.register(() => {
			my.eventHandler.trigger("setListView");
		});

		my.breadcrumbs.onClick((folder) => {
			my.triggerNavigateTo(folder);
		});
	}

	return that;
}

export default abstractFolderView;
